import React from "react";

import Fetcher from "../common/Fetcher";
import CartItem, { RemainderItem, HardwareItem, isCabinetItem, createHardwareItemsFromPlan } from "./CartItem";
import type { IHardwarePlanEntry } from "./CartItem";
import PrestaGlobals from "../common/PrestaGlobals";
import DimensionUtils from "../common/DimensionUtils";
import ICabinetResult from "../unityView/interfaces/ICabinetResult";
import IOrder from "../unityView/interfaces/IOrder";
import IParamScript from "../database/interfaces/paramScript/IParamScript";

import { DatabaseContext } from "../database/DatabaseHandler";
import { ParametersContext } from "../parametersProcessor/ParametersProcessor";
import { createCdnThumbnailUrl } from "../../config/cdn";
import prestaGlobals from "../common/PrestaGlobals";
import { parseNameSuffix, scriptDesignName } from "../common/NameUtils";
import { translateElementName } from "../i18n/translateElementName";
import QuoteErrorDialog from "./QuoteErrorDialog";

// Keys for session storages
const sessionStorageKeyName: string = "ucjson";
const sessionStorageStampKeyName: string = "ucjsonstamp";
// A design whose scripts name nothing: not the loaded product's name, it may be anything else
const DESIGN_FALLBACK_NAME: string = "Egyedi terv";

/**
 * A gyerek-darabok LongName-je a gyökér nevét hordozza prefixként ("szekrény 1.Front.Board").
 * Ha a gyökér nevet átírjuk (pl. a Kosárba gomb automatikus sorszámozása "szekrény 1" → "szekrény 2"),
 * a gyerekek prefixét is frissíteni kell, különben a megrendelés-detailen a régi név marad a darabokon.
 * A prefixet a "." határolóval együtt nézzük, hogy "szekrény 1" ne illeszkedjen a "szekrény 10"-re.
 */
const relabelChildrenLongName = (children: IOrder[], oldRoot: string, newRoot: string): IOrder[] => {
    if (!oldRoot || oldRoot === newRoot) {
        return children;
    }
    const prefix = oldRoot + ".";
    return children.map((child) => {
        if (child.LongName && child.LongName.startsWith(prefix)) {
            return { ...child, LongName: newRoot + child.LongName.slice(oldRoot.length) };
        }
        return child;
    });
};

export interface IQuoteResult {
    url: string;
    urlLong?: string;
    error?: string;
    updated?: boolean;
}

/** Existing quote of the admin's working cart (prefill) */
export interface IQuoteSaved {
    url: string;
    urlLong?: string;
    total: number;
    email: string;
    validUntil: string;
    linkExpiresAt: string;
    guestMode: 0 | 1 | 2;
    description: string;    // Markdown
    priceInfo: string;      // Markdown
    imageUrl: string;       // '' = none
}

/** Quote properties as sent by UCC (quoteFrontendInfo); e-mail not exposed */
export interface IQuoteInfo {
    id: number;
    validUntil: string | null;
    linkExpiresAt: string | null;
    guestMode: 0 | 1 | 2;
    emailRestricted: boolean;
    priceValid: boolean;
    priceVisible: boolean;
    orderable: boolean;
    description: string;    // Markdown, '' = none
    priceInfo: string;      // Markdown, '' = none
}

/** Quote cart shown in the quote bar; total = gross, null when hidden */
export interface IQuoteCart {
    id: number;
    reference: string;
    total: number | null;
}

export interface IQuoteInput {
    total: number;
    email: string;
    validUntil: string;      // '' | 'YYYY-MM-DD'
    linkExpiresAt: string;   // '' | 'YYYY-MM-DD'
    guestMode: 0 | 1 | 2;
    description: string;     // Markdown
    priceInfo: string;       // Markdown
    image: File | null;      // new preview image
    removeImage: boolean;
}

const QUOTE_ERRORS: Record<string, string> = {
    expired: 'Ez az árajánlat link lejárt.',
    login: 'Ezt az árajánlatot csak a címzett tekintheti meg. Jelentkezzen be a megfelelő e-mail címmel.',
    notorderable: 'Ez az árajánlat nem rendelhető meg.',
};

const QUOTE_LOAD_FAILED = 'Az árajánlat betöltése sikertelen.';
const QUOTE_ORDER_FAILED = 'Az árajánlat kosárba helyezése sikertelen.';

// Full page load: leaving the quote layout would remount Unity
const goHome = (): void => {
    window.location.replace(PrestaGlobals.virtualUri + '/');
};

/** Context for handling instant cart data */
export const CartContext = React.createContext<{
    cartName: string;
    cartItems: (CartItem | RemainderItem)[];
    editedItem: CartItem | null;
    cabinetResult: ICabinetResult | null;
    setCabinetResult: (result: ICabinetResult | null) => void;
    updateCartName: (name: string) => void;
    saveCartName: () => void;
    updateCart: (items: (CartItem | RemainderItem)[]) => void;
    recalcCartItems: () => void;
    getTotalItemCount: () => number;
    getTotalPrice: () => number;
    getCurrentConfigPrice: () => number;
    setCurrentConfigPrice: (price: number) => void;
    setCurrentConfigItem: (item: CartItem | null) => void;
    getOpData: () => any;
    opDataRealState: boolean;
    opDataRealFlag: boolean;
    notifyStyleScriptSent: () => void;
    checkout: () => void;
    editItem: (item: CartItem | null) => void;
    deleteItem: (item: CartItem) => void;
    addCurrentItem: () => void;
    updateItemCount: (item: CartItem, newCount: number) => void;
    removeAllRemainderItems: () => void;
    hasHardwareError: () => boolean;
    hardwarePlan: IHardwarePlanEntry[] | null;
    clearCart: () => void;
    cartLoading: boolean;
    setCartLoading: (loading: boolean) => void;
    getQuickAndDirtyPrice: () => void;
    generateShareLink: () => Promise<string>;
    cartSaving: boolean;
    saveCart: () => Promise<boolean>;
    isQuote: boolean;
    quote: IQuoteInfo | null;
    quoteCart: IQuoteCart | null;
    createQuote: (input: IQuoteInput) => Promise<IQuoteResult>;
    getSavedQuote: () => Promise<IQuoteSaved | null>;
    deleteQuote: () => Promise<{ deleted: number; error?: string }>;
    orderQuote: () => Promise<void>;
    /**
     * The AI designer page: the ucon the Designer shows when it is not the product's, or null,
     * and the name its scripts give it (scriptDesignName). While set, a cart line stores this ucon
     * and a descriptor with the name alone.
     */
    setDesignUcon: (ucon: any | null, name?: string | null) => void;
    /** Admin StyleScript override; while set, Unity gets it instead of the generated one. */
    designStyle: any[] | null;
    setDesignStyle: (style: any[] | null) => void;
    /** Admin edited the ucon or style: a cart line stores both scripts and a name-only descriptor. */
    adminDesign: boolean;
    setAdminDesign: (value: boolean) => void;
    /** Replaces the cart with the current design alone, then saves it as a quote. */
    quoteCurrentDesign: (input: IQuoteInput) => Promise<IQuoteResult>;
}>({
    cartName: "",
    cartItems: [],
    editedItem: null,
    cabinetResult: null,
    setCabinetResult: () => {},
    updateCartName: () => {},
    saveCartName: () => {},
    updateCart: () => {},
    recalcCartItems: () => {},
    getTotalItemCount: () => 0,
    getTotalPrice: () => 0,
    getCurrentConfigPrice: () => 0,
    setCurrentConfigPrice: () => {},
    setCurrentConfigItem: () => {},
    getOpData: () => null,
    opDataRealState: false,
    opDataRealFlag: false,
    notifyStyleScriptSent: () => {},
    checkout: () => 0,
    editItem: () => 0,
    deleteItem: () => 0,
    addCurrentItem: () => 0,
    updateItemCount: () => 0,
    removeAllRemainderItems: () => {},
    hasHardwareError: () => false,
    hardwarePlan: null,
    clearCart: () => {},
    cartLoading: false,
    setCartLoading: () => {},
    getQuickAndDirtyPrice: () => {},
    generateShareLink: async () => '',
    cartSaving: false,
    saveCart: async () => true,
    isQuote: false,
    quote: null,
    quoteCart: null,
    createQuote: async () => ({ url: '' }),
    getSavedQuote: async () => null,
    deleteQuote: async () => ({ deleted: 0 }),
    orderQuote: async () => {},
    setDesignUcon: () => {},
    designStyle: null,
    setDesignStyle: () => {},
    adminDesign: false,
    setAdminDesign: () => {},
    quoteCurrentDesign: async () => ({ url: '' }),
});

/**
 * Handles the instant cart functionality
 */
const CartHandler: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const dataContext = React.useContext(DatabaseContext);
    const parametersContext = React.useContext(ParametersContext);

    // Use local state to hold data and updater
    const [cartItems, setCartItems] = React.useState<(CartItem | RemainderItem)[]>([]);
    const [editedItem, setEditedItem] = React.useState<CartItem | null>(null);
    const [cartName, setCartName] = React.useState<string>(
        PrestaGlobals.cartReference !== "" ? PrestaGlobals.cartReference : ("#" + PrestaGlobals.cartId)
    );
    const [currentConfigItem, setCurrentConfigItemState] = React.useState<CartItem | null>(null);
    const [currentConfigPrice, setCurrentConfigPriceState] = React.useState<number>(0);
    const [cartLoading, setCartLoading] = React.useState<boolean>(false);
    const [cartSaving, setCartSaving] = React.useState<boolean>(false);
    const [cabinetResult, setCabinetResult] = React.useState<ICabinetResult | null>(null);
    const [opData, setOpData] = React.useState<any>(null);
    const [opDataRealState, setOpDataRealState] = React.useState<boolean>(false);
    const [opDataRealFlag, setOpDataRealFlag] = React.useState<boolean>(false);
    const [hwData, setHwData] = React.useState<IHardwarePlanEntry[] | null>(null);
    // Quote cart: read-only, no save / recalculation from the designer
    const [isQuote, setIsQuoteState] = React.useState<boolean>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        // Decided up front so the layout (and Unity) doesn't remount after load
        if (PrestaGlobals.quoteToken) return true;
        return PrestaGlobals.isQuote && !urlParams.get('share') && !urlParams.get('cart');
    });
    const isQuoteRef = React.useRef<boolean>(isQuote);
    // Quote flags for later layout use (price / order display)
    const [quote, setQuote] = React.useState<IQuoteInfo | null>(null);
    const [quoteCart, setQuoteCart] = React.useState<IQuoteCart | null>(null);
    // Quote carts are read-only for customers; admins may change anything
    const quoteReadOnly = (): boolean => isQuoteRef.current && !PrestaGlobals.isAdmin;
    const setIsQuote = (value: boolean): void => {
        isQuoteRef.current = value;
        setIsQuoteState(value);
    };
    // Quote access error dialog; OK -> home page
    const [quoteError, setQuoteError] = React.useState<string | null>(null);
    const showQuoteError = (code: string): void => setQuoteError(QUOTE_ERRORS[code] ?? code);

    // Ref to hold the latest cart items for use in event handlers
    const cartItemsRef = React.useRef<(CartItem | RemainderItem)[]>(cartItems);
    // Ref to hold the latest cart name for use in event handlers
    const cartNameRef = React.useRef<string>(cartName);

    // Hány SetStyleScript-küldésre várunk még Unity-választ (UCOrderReceived).
    // Amíg > 0, a cabinetResult ELAVULT: pl. a Kosárba gomb blur-je frissítette a
    // paramétert (méret stb.), a Unity már megkapta, de még nem számolt újra.
    const pendingUnityResults = React.useRef(0);
    // Volt-e kosárba-kérés, amit a friss cabinetResult megérkezéséig halasztunk.
    const pendingCommitRef = React.useRef(false);
    // Quote save waiting for a fresh Unity result
    const unityWaiterRef = React.useRef<(() => void) | null>(null);
    const cabinetResultRef = React.useRef<ICabinetResult | null>(null);
    // The AI designer page's ucon, when the Designer shows a design that is not the product's
    // (setDesignUcon). A ref: createCartData reads it in the same commit that sets it.
    const designUconRef = React.useRef<any | null>(null);
    const designNameRef = React.useRef<string | null>(null);
    // Admin script design (setDesignStyle / setAdminDesign); refs for createCartData, state for renders
    const [designStyle, setDesignStyleState] = React.useState<any[] | null>(null);
    const designStyleRef = React.useRef<any[] | null>(null);
    const [adminDesign, setAdminDesignState] = React.useState<boolean>(false);
    const adminDesignRef = React.useRef<boolean>(false);
    const setDesignStyle = (style: any[] | null): void => {
        designStyleRef.current = style;
        setDesignStyleState(style);
    };
    const setAdminDesign = (value: boolean): void => {
        adminDesignRef.current = value;
        setAdminDesignState(value);
    };

    // Gyors-ár backend hívás fékezés
    const QUICK_PRICE_DEBOUNCE_MS = 500;
    const quickPriceTimer = React.useRef<number | undefined>(undefined);
    const quickPriceAbort = React.useRef<AbortController | null>(null);

    React.useEffect(() => () => {
        window.clearTimeout(quickPriceTimer.current);
        quickPriceAbort.current?.abort();
    }, []);

    // Update and return the current timestamp (used for cart item additions)
    function getTimestamp(): number {
        PrestaGlobals.savedTimestamp = Math.floor(new Date().getTime() / 1000);
        return PrestaGlobals.savedTimestamp;
    }

    const updateCurrentConfigPrice = React.useCallback((price: number) => {
        setCurrentConfigPriceState(price);
    }, []);

    const updateCurrentConfigItem = React.useCallback((item: CartItem | null) => {
        setCurrentConfigItemState(item);
        setCurrentConfigPriceState(item ? item.PriceGross : 0);
    }, []);

    // Get the item price from backend and add the item to cart when ready
    // Set index if the item is already in the cart and we just want to update it, otherwise backend will add it as new item
    const getPriceForCartItem = (item?: CartItem, index?: number): void => {
        if (quoteReadOnly()) return;

        setCartLoading(true);
        const currentCartItems = cartItemsRef.current.filter(isCabinetItem);
        const url: string = PrestaGlobals.cartUrl("recalccartquickprice");
        const f = new Fetcher<any>(url, "POST");
        let payload = "payload=" + encodeURIComponent(JSON.stringify(currentCartItems));
        if (item) {
            payload += "&item=" + encodeURIComponent(JSON.stringify(item));
        }
        f.request(payload, (response: any) => {
            if (response === null) {
                console.error("Failed to get price for cart item!");
                return;
            }

            setOpData(response.opcut);
            setHwData(response.hardware ?? null);
            const derived = cartItemsRef.current.filter(ci => !isCabinetItem(ci));
            const newCartItems = [...(response.cart as CartItem[]).sort((a, b) => b.id_cabinet - a.id_cabinet), ...derived];
            cartItemsRef.current = newCartItems;
            setCartItems(newCartItems);
            saveCartToDatabase(false);
        // A Fetcher hálózati hibánál nem hívja a callbacket — a betöltés-jelzőt minden esetben levesszük.
        }).finally(() => setCartLoading(false));
    }

    const getQuickAndDirtyPriceForConfigItem = (item: CartItem): void => {
        item.count = 1;
        // Debounce: gyors egymásutáni param-változásokból csak az utolsó megy ki a backendre.
        window.clearTimeout(quickPriceTimer.current);
        quickPriceTimer.current = window.setTimeout(() => {
            // A még függő korábbi kérést eldobjuk — csak a legfrissebb ár érdekes.
            quickPriceAbort.current?.abort();
            const controller = new AbortController();
            quickPriceAbort.current = controller;

            const url: string = PrestaGlobals.cartUrl("getprice");
            const f = new Fetcher<any>(url, "POST");
            let payload = "item=" + encodeURIComponent(JSON.stringify(item));
            f.request(payload, (responseCartItem: CartItem) => {
                if (responseCartItem === null) {
                    console.error("Failed to get price for cart item!");
                    return;
                }
                updateCurrentConfigItem(responseCartItem);
            }, controller.signal);
        }, QUICK_PRICE_DEBOUNCE_MS);
    }

    /**
     * Egyedi kosár-elemnevet generál.
     * - Számozott név ("Polc 2"): csak akkor számozzuk át, ha PONT ez a név már foglalt;
     *   ilyenkor a számától +1-ről indulva a következő szabad sorszámot kapja.
     *   "Polc 1" a kosárban + új "Polc 2" → szabad → marad "Polc 2"
     *   "Polc 1" a kosárban + új "Polc 1" → foglalt → "Polc 2"
     * - Számozatlan név: csak akkor számozzuk, ha a szótő (base) ütközik egy kosárbeli
     *   elem szótövével; ilyenkor 1-től az első szabad sorszámot kapja (kitölti a rést).
     *   "Polc"         + új "Szekrényem" → nincs ütközés → marad "Szekrényem"
     *   "Szekrényem"   + új "Szekrényem" → ütközik       → "Szekrényem 1"
     *   "Szekrényem 1" + új "Szekrényem" → ütközik       → "Szekrényem 2"
     *   "Szekrényem 2" + új "Szekrényem" → ütközik       → "Szekrényem 1" (kitölti a rést)
     * Az extraTakenNames-ben átadott (még kosárba nem került) nevek is foglaltnak
     * számítanak — így kérhető le a KÖVETKEZŐ szabad név a form név-mezője számára.
     */
    const generateUniqueCartItemName = (rawName: string, extraTakenNames: string[] = []): string => {
        const takenNames = cartItemsRef.current
            .filter(isCabinetItem)
            .map(ci => ci.Name)
            .concat(extraTakenNames);
        const canon = (name: string) => {
            const { base, num } = parseNameSuffix(name);
            return JSON.stringify([base, num]);
        };
        const takenKeys = new Set(takenNames.map(canon));
        const nameExists = (name: string) => takenKeys.has(canon(name));
        const { base, num, formatter } = parseNameSuffix(rawName);

        if (num !== null) {
            // A user szándékosan számozta a nevet → tiszteletben tartjuk, amíg szabad.
            if (!nameExists(rawName)) return rawName;
            let i = num + 1;
            while (nameExists(formatter(i))) {
                i++;
            }
            return formatter(i);
        }

        const baseCollides = takenNames.some(name => parseNameSuffix(name).base === base);
        if (!baseCollides) return rawName;

        let i = 1;
        while (nameExists(formatter(i))) {
            i++;
        }
        return formatter(i);
    };

    const syncNameToForm = (finalName: string): void => {
        // An AI design is not built from the parameters: a Name change would re-send the
        // product's StyleScript over it. Under a style override the name is the override's, not the form's.
        if (designUconRef.current != null || designStyleRef.current != null) return;
        const params = parametersContext.parameters;
        if (!params) return;

        const nameParamKey = Object.keys(params).find(
            key => params[key]?.Name !== undefined && params[key]?.TargetType === undefined
        );
        if (!nameParamKey) return;

        parametersContext.updateParameters(nameParamKey, "Name", finalName);
    };

    // The descriptor of an AI design's cart line: the Name entry alone, so the line has its name,
    // and the server its product (descriptor[0].productId: the line's product and image). Built
    // on the product's own Name script when there is one. The StyleScript is not stored yet, so
    // the line does not open again as the design.
    const createNameDescriptor = (name: string, paramScripts: IParamScript[] | null): IParamScript[] => {
        const template = paramScripts?.find(s =>
            s.parameterProperty === "Name" && s.targetPath === "" && s.targetType === null
        );
        const base: IParamScript = template ?? {
            id: "", productId: "", typeDescriptor: {} as IParamScript["typeDescriptor"], tab: "configuration",
            title: "", panel: null, sortIndex: 0, applyIndex: null, editable: 1, targetPath: "", targetType: null,
            parameterName: "Name", parameterType: null, parameterProperty: "Name", parameterPropertyIndex: null,
            composite: 0, parameterValue: "", shopId: null, customerId: null, cartId: null,
        };
        return [{ ...base, productId: String(PrestaGlobals.productId), parameterValue: name }];
    };

    // Admin edited the ucon or the style (the AI designer's ucon is not one)
    const isScriptDesign = (): boolean =>
        designUconRef.current == null && (adminDesignRef.current || designStyleRef.current != null);

    // Creates a cart item for "add" or "update" cart requests based on a cabinet result
    const createCartData =(id: number, cartAction: "add" | "update", cabinetResult: ICabinetResult, dataContext: React.ContextType<typeof DatabaseContext>): CartItem | null => {
        const designUcon = designUconRef.current;
        if (designUcon == null && (dataContext.uconScript == null || dataContext.paramScripts == null)) {
            console.error("Database context is not fully loaded!");
            return null;
        }
        if (cabinetResult.RootSize == null) {
            console.error("Cabinet result does not have RootSize!");
            return null;
        }

        const cartItem: CartItem = new CartItem();
        cartItem.id_cabinet = id;
        cartItem.cartAction = cartAction;

        // On the AI designer page the line stores the design's ucon and the order Unity built from
        // it (below); see createNameDescriptor.
        cartItem.uconScript = designUcon ?? dataContext.uconScript;
        // A design is named by its own scripts, never by the loaded product: it may be a whole room.
        if (isScriptDesign()) {
            // Admin script design: the scripts Unity built it from, read back as-is on reopen. Without
            // a style override the generated script's root Name is the form's name.
            cartItem.styleScript = designStyleRef.current ?? parametersContext.generateStyleScript();
            cartItem.descriptor = createNameDescriptor(
                scriptDesignName(cartItem.uconScript, cartItem.styleScript) || cabinetResult.RootName || DESIGN_FALLBACK_NAME,
                dataContext.paramScripts
            );
        } else {
            cartItem.descriptor = designUcon != null
                ? createNameDescriptor(designNameRef.current || cabinetResult.RootName || DESIGN_FALLBACK_NAME, dataContext.paramScripts)
                : parametersContext.generateParameterScripts();
        }

        // A nevet a frissen commitolt Name-paraméterből (descriptor) olvassuk, NEM a
        // cabinetResult.RootName-ből — utóbbi csak Unity-kör után frissül, így ha a név-mezőből
        // blur nélkül nyomjuk a Kosárba gombot, a RootName még a régi nevet adná. A descriptor
        // viszont a generateParameterScripts pillanatában már a commitolt értéket tartalmazza.
        const nameEntry = cartItem.descriptor.find(d =>
            d.parameterProperty === "Name" && d.targetPath === "" && d.targetType === null
        );
        const rawName = (
            (typeof nameEntry?.parameterValue === "string" ? nameEntry.parameterValue : "")
            || cabinetResult.RootName
            || PrestaGlobals.productName
        ).trim();
        // Az egyediesítést NEM itt végezzük: csak ténylegesen új elemnél kell (lásd addCurrentItem),
        // mert ha a geometria ÉS a név is egyezik egy meglévővel, akkor összevonunk (nyers néven).
        cartItem.Name = rawName;

        // A gyerek-darabok LongName-je a Unity gyökér-nevét (RootName) hordozza prefixként
        // ("cekrényke 1.Front.Board"). Ez — a RootName-hez hasonlóan — csak Unity-kör után
        // frissül, ezért egy átnevezés + azonnali Kosárba esetén a stale cabinetResult-ból az
        // ELŐZŐ nevet kapnánk a megrendelés-detailen. A látható item.Name-et a descriptorból
        // olvassuk (lásd fent), így a LongName prefixét is ugyanahhoz a commitolt névhez igazítjuk.
        const oldRoot = cabinetResult.RootName;
        // cartItem.children = cabinetResult.Order; //[EA - 337]
        const translatedChildren = cabinetResult.Order.map((child) => {
            const newChild = { ...child };
            if (newChild.Name) {
                newChild.Name = translateElementName(newChild.Name);
            }
            return newChild;
        });
        cartItem.children = relabelChildrenLongName(translatedChildren, oldRoot, rawName);

        cartItem.Dimensions = DimensionUtils.formatDimension(cabinetResult.RootSize.Width, "")
            + "x" + DimensionUtils.formatDimension(cabinetResult.RootSize.Height, "")
            + "x" + DimensionUtils.formatDimension(cabinetResult.RootSize.Depth, "");

        return cartItem;
    };

    // Creates RemainderItems derived from OpData (leftover material after cutting)
    const createRemainderItemsFromOpData = (opData: any): RemainderItem[] => {
        if (!opData) return [];
        const items: RemainderItem[] = [];
        for (const key of Object.keys(opData)) {
            const entry = opData[key];
            if (!entry.extraData) continue;
            const { name, price, priceGross, totalUnusedArea } = entry.extraData;
            if (!totalUnusedArea || totalUnusedArea <= 0) continue;
            const item = new RemainderItem();
            item.Name = name || "";
            item.Price = price || 0;
            item.PriceGross = priceGross || 0;
            item.unusedArea = totalUnusedArea;
            item.Dimensions = ""+Math.round(totalUnusedArea);
            item.count = 1;
            item.image = createCdnThumbnailUrl(entry.ViewData.ProductImage);
            items.push(item);
        }
        return items;
    };

    // Saves cart items to database and redirects to checkout page if selected
    const saveCartToDatabase = (redirect: boolean): void => {
        if (quoteReadOnly()) {
            if (redirect) location.href = PrestaGlobals.cartURL;
            return;
        }
        // Use ref to get the latest cart items, excluding derived lines (backend-unaware)
        const currentCartItems = cartItemsRef.current.filter(isCabinetItem);
        const url: string = PrestaGlobals.cartUrl("save");
        const f = new Fetcher<any>(url, "POST");
        f.request("payload=" + encodeURIComponent(JSON.stringify(currentCartItems)), (response: any) => {
            localStorage.setItem(sessionStorageStampKeyName, Date.now().toString());
            // Ignore response for now, just redirect to cart/checkout page
            if (redirect) {
                location.href = PrestaGlobals.cartURL;
            }
        });
    };

    // Persists the cart to the DB, then finalizes it server-side (opcut processing),
    // mirroring the cart page logic — but in place, without leaving the designer.
    // Returns false if the save or the server-side processing failed
    const saveCart = async (): Promise<boolean> => {
        if (quoteReadOnly()) return true;
        setCartSaving(true);
        try {
            saveCartNameToDatabase();

            const currentCartItems = cartItemsRef.current.filter(isCabinetItem);
            const saveUrl = PrestaGlobals.cartUrl("save");
            const saved = await fetch(saveUrl, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: "payload=" + encodeURIComponent(JSON.stringify(currentCartItems)),
            });
            if (!saved.ok) return false;
            localStorage.setItem(sessionStorageStampKeyName, Date.now().toString());

            // Finalize server-side only if not already processed
            const stamp = localStorage.getItem(sessionStorageStampKeyName);
            if (stamp && stamp !== "false") {
                const processUrl = PrestaGlobals.cartUrl("processsessioncartserverstyle");
                const processed = await fetch(processUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: "ucjsonstamp=" + stamp,
                });
                // A PHP fatal (e.g. in opcut) comes back as non-JSON or a non-array
                const result = processed.ok ? await processed.json().catch(() => null) : null;
                if (!Array.isArray(result)) return false;
                localStorage.setItem(sessionStorageStampKeyName, "false");
            }
            return true;
        } catch (error) {
            console.error("Error saving cart:", error);
            return false;
        } finally {
            setCartSaving(false);
        }
    };

    // Saves cart name to database
    const saveCartNameToDatabase = (): void => {
        const currentCartName = cartNameRef.current;
        if (currentCartName === "") {
            return;
        }
        const url: string = PrestaGlobals.cartUrl("renamecartreference");
        const f = new Fetcher<any>(url, "POST");
        const renameData = {
            cartId: PrestaGlobals.cartId,
            orderName: currentCartName,
        };
        f.request("payload=" + encodeURIComponent(JSON.stringify(renameData)), (response: any) => {
            // Ignore response for now
        });
    };

    // A jelenlegi konfiguráció kosárba "commitolása". Két helyről hívjuk, és pontosan ugyanúgy
    // kell viselkednie: (1) a Kosárba/Mentés gomb (addCurrentItem), és (2) szerkesztés közben a
    // ceruza-icon ismételt megnyomása (EditOffIcon = szerkesztés befejezése). Ha editedItem !== null,
    // a meglévő elemet frissíti (update + esetleges merge), különben új elemként veszi fel.
    const commitCurrentConfiguration = (): void => {
        if (cabinetResult == null) {
            console.log("No cabinet result available to add to cart.");
            return;
        }
        if (editedItem !== null) {
            const cartItem: CartItem | null = createCartData(
                editedItem.id_cabinet,
                "update",
                cabinetResult!,
                dataContext
            );

            if(cartItem == null) {
                console.log("Current cart item could not be created for update!");
                return;
            }
            // A design's scripts carry its name without the number the cart gave it: keep the number.
            if ((isScriptDesign() || designUconRef.current != null)
                && parseNameSuffix(editedItem.Name).base === parseNameSuffix(cartItem.Name).base) {
                cartItem.children = relabelChildrenLongName(cartItem.children, cartItem.Name, editedItem.Name);
                cartItem.Name = editedItem.Name;
                cartItem.descriptor = createNameDescriptor(editedItem.Name, dataContext.paramScripts);
            }
            // Preserve count when updating
            cartItem.count = editedItem.count;
            delete cartItem.hash;
            const newHash = CartItem.getHash(cartItem);
            // MERGE LOGIC
            const duplicate = cartItemsRef.current.find(ci => {
                if (!isCabinetItem(ci)) return false;
                if (ci === editedItem) return false;
                delete (ci as CartItem).hash;
                return CartItem.getHash(ci as CartItem) === newHash;
            }) as CartItem | undefined;

            if (duplicate) {
                const mergedItems = cartItemsRef.current
                    .filter(ci => ci !== editedItem)
                    .map(ci => {
                        if (ci === duplicate) {
                            // Geometria ÉS név egyezik (a hash mindkettőt nézi) → összevonjuk a
                            // darabszámot, és a szerkesztett elem nevét/descriptorát írjuk a megmaradó sorra.
                            return { ...ci, count: ci.count + editedItem.count, Name: cartItem.Name, descriptor: cartItem.descriptor, hash: undefined } as CartItem;
                        }
                        return ci;
                    });
                setCartItems(mergedItems);
                cartItemsRef.current = mergedItems;
                setOpDataRealState(false);
                getPriceForCartItem();
                setEditedItem(null);
                return;
            }

            // Refresh the cart item with updated data
            getPriceForCartItem(cartItem, cartItemsRef.current.indexOf(editedItem));
            setEditedItem(null);

            return;
        }

        const cartItem: CartItem | null = createCartData(getTimestamp(), "add", cabinetResult, dataContext);
        if(cartItem == null) {
            console.log("Current cart item could not be created!");
            return;
        }

        // Check whether the cabinet is already in the cart, and update count if so.
        // A hash a geometriát ÉS a nevet (trimmelt, exact) együtt nézi → ide csak akkor fut be,
        // ha méret/dekor ÉS név is egyezik egy meglévő sorral → ilyenkor növeljük a darabszámot.
        // Eltérő név esetén nincs találat → lentebb új sorként kerül a kosárba.
        // A merge-lookupot a ref-en (szinkron) végezzük, NEM a lemaradó cartItemMap state-en:
        // az optimistán már betett elem így AZONNAL látszik a következő Kosárba-kattintásnál.
        const newItemHash = CartItem.getHash(cartItem);
        const existingItem = cartItemsRef.current.find(
            ci => isCabinetItem(ci) && CartItem.getHash(ci as CartItem) === newItemHash
        ) as CartItem | undefined;
        if (existingItem !== undefined) {
            const updatedItems = cartItemsRef.current.map(ci => {
                if (ci === existingItem) {
                    return { ...ci, count: ci.count + 1, Name: cartItem.Name, descriptor: cartItem.descriptor, hash: undefined };
                }

                return ci;
            });
            setCartItems(updatedItems);
            cartItemsRef.current = updatedItems;
            setOpDataRealState(false);
            getPriceForCartItem();
            return;
        }
        setOpDataRealState(false);
        // Ténylegesen új elem → most egyediesítjük a nevet (ütközés esetén sorszámozzuk).
        const uniqueName = generateUniqueCartItemName(cartItem.Name);
        // A gyerek-darabok LongName prefixét a createCartData még a NYERS (descriptorbeli) névvel
        // állította be. Az egyediesítés után ezt is át kell írni, különben a megrendelés-detailen
        // "szekrény 1.Front.Board" marad, miközben az elem neve már "szekrény 2".
        cartItem.children = relabelChildrenLongName(cartItem.children, cartItem.Name, uniqueName);
        cartItem.Name = uniqueName;
        // A getHash a fenti lookupkor a NYERS névvel beégette a hash-t a cartItem.hash-be;
        // mivel a név most változott (és a név része a hashnek), invalidálni kell, hogy
        // legközelebb a végleges névvel számolódjon újra.
        delete cartItem.hash;
        // New item, set initial count
        cartItem.count = 1;
        // Optimistán, a backend-kör ELŐTT betesszük a kosárba + a ref-be, hogy egy gyors
        // következő Kosárba-kattintás már EZT találja meg (getHash) és a darabszámot növelje,
        // ne új sort nyisson. A backend-válasz úgyis lecseréli az elemet az árazott verzióra
        // (id_cabinet szerint dedupál), így nem duplázódik.
        const withNew = [...cartItemsRef.current, cartItem];
        cartItemsRef.current = withNew;
        setCartItems(withNew);
        // Get price from backend and refresh with the priced version when ready
        getPriceForCartItem(cartItem);
        syncNameToForm(cartItem.Name);
    };

    // A commit LEGFRISSEBB closure-je — a halasztott hívásokhoz (effect / timeout), hogy
    // azok is a mindenkori aktuális state-tel (cabinetResult, editedItem...) fussanak.
    const commitRef = React.useRef(commitCurrentConfiguration);
    commitRef.current = commitCurrentConfiguration;

    // HALASZTOTT KOSÁRBA-TÉTEL: ha a Kosárba/Mentés gomb megnyomásakor még Unity-válaszra
    // vártunk (a gomb blur-je épp most commitolt egy input-értéket → SetStyleScript ment ki),
    // a commit ide halasztódik, és a FRISS cabinetResult megérkezésekor fut le — így a
    // méret/darab adatok már az utolsó beírt értéket tükrözik (nem a klikk előtti állapotot).
    React.useEffect(() => {
        cabinetResultRef.current = cabinetResult;
        if (unityWaiterRef.current && pendingUnityResults.current === 0) {
            unityWaiterRef.current();
        }
        if (!pendingCommitRef.current) return;
        if (pendingUnityResults.current > 0) return; // van még függő Unity-kör — várunk
        if (cabinetResult == null) return;           // hibás/üres eredmény — várunk tovább
        pendingCommitRef.current = false;
        commitRef.current();
    }, [cabinetResult]);

    // Update context value using useMemo to avoid unnecessary renders
    const cartContextValue = React.useMemo(() => ({
        cartName,
        updateCartName: (name: string) => {
            setCartName(name);
        },
        saveCartName: () => {
            saveCartNameToDatabase();
        },
        cartItems,
        updateCart: (items: (CartItem | RemainderItem)[]) => {
            setCartItems(items);
        },
        recalcCartItems: () => {
            updateCartFinalPrice().then((items: CartItem[]) => {
                const sorted = items.sort((a, b) => b.id_cabinet - a.id_cabinet);
                // Preserve derived lines already set by the opData / hwData effects
                setCartItems(prev => {
                    const derived = prev.filter(ci => !isCabinetItem(ci));
                    return [...sorted, ...derived];
                });
                const derived = cartItemsRef.current.filter(ci => !isCabinetItem(ci));
                cartItemsRef.current = [...sorted, ...derived];
                saveCartToDatabase(false);
            })
        },
        editedItem: editedItem || null,
        cabinetResult,
        setCabinetResult: (result: ICabinetResult | null) => {
            // Egy függő Unity-kör lezárult (UCOrderReceived) — a számláló csökken. A clamp a
            // Unity saját kezdeményezésű (küldés nélküli) eredményei ellen véd.
            pendingUnityResults.current = Math.max(0, pendingUnityResults.current - 1);
            setCabinetResult(result);
        },
        notifyStyleScriptSent: () => {
            pendingUnityResults.current++;
        },
        setDesignUcon: (ucon: any | null, name: string | null = null) => {
            designUconRef.current = ucon;
            designNameRef.current = ucon != null ? name : null;
        },
        designStyle,
        setDesignStyle,
        adminDesign,
        setAdminDesign,
        quoteCurrentDesign,
        getTotalItemCount: () => {
            // Cabinet count only — hardware and remainder lines excluded.
            return cartItems.reduce((total, item) => isCabinetItem(item) ? total + item.count : total, 0);
        },
        getTotalPrice: () => {
            // Cabinets + all hardware (bagged and per-piece) from the plan; remainder excluded.
            const cabinets = cartItems.reduce((total, item) => isCabinetItem(item) ? total + item.PriceGross * item.count : total, 0);
            const hardware = (hwData ?? []).reduce((total, entry) => total + (entry.error ? 0 : (entry.totalGross || 0)), 0);
            return cabinets + hardware;
        },
        getCurrentConfigPrice: () => {
            return currentConfigPrice;
        },
        setCurrentConfigPrice: (price: number) => {
            updateCurrentConfigPrice(price);
        },
        setCurrentConfigItem: (item: CartItem | null) => {
            updateCurrentConfigItem(item);
        },
        removeAllRemainderItems: () => {
            const updatedItems = cartItems.filter(item => !(item instanceof RemainderItem));
            setCartItems(updatedItems);
            cartItemsRef.current = updatedItems;
            saveCartToDatabase(false);
        },
        hasHardwareError: () => {
            return (hwData ?? []).some(entry => entry.error !== null);
        },
        hardwarePlan: hwData,

        opDataRealState,
        opDataRealFlag,
        getOpData: () => {
            let priceGrossTotal = 0;
            if(opData) {
                for (const [key, val] of Object.entries(opData)) {
                    if(opData[key].extraData && opData[key].extraData.priceGross)
                        priceGrossTotal += opData[key].extraData.priceGross;
                }
            }
            return priceGrossTotal;
        },

        checkout: () => {
            if ((hwData ?? []).some(entry => entry.error !== null)) {
                console.warn("Checkout blocked: hardware without price.");
                return;
            }
            saveCartNameToDatabase();
            saveCartToDatabase(true); // True means redirect to checkout after saving
        },

        getQuickAndDirtyPrice: () => {
            if (cabinetResult == null) {
                console.log("No cabinet result available to add to cart.");
                return;
            }
            const cartItem: CartItem | null = createCartData(getTimestamp(), "add", cabinetResult, dataContext);
            if (cartItem == null) {
                console.log("No cabinet result available to add to cart.");
                return;
            }

            getQuickAndDirtyPriceForConfigItem(cartItem);
        },

        editItem: (item: CartItem | null) => {
            if (item !== null && item === editedItem) {
                setEditedItem(null);
                return;
            }
            // Set the edited item in state
            setEditedItem(item === editedItem ? null : item);
            if (item == null) {
                // Exit edit mode
                return;
            }
            // id_cabinet is the unique identifier of the cabinet in the cart
            // generated when the cabinet was added to the cart, it's a timestamp
            // here is how we restore it from the instant cart
            PrestaGlobals.savedTimestamp = item.id_cabinet;
            PrestaGlobals.productName = item.Name;
            setOpDataRealState(false);
            restoreDesignScripts(item);
            dataContext.updateScripts(item.descriptor, item.uconScript);
        },
        deleteItem: (item: CartItem) => {
            if (!isCabinetItem(item)) return;
            if (editedItem === item) {
                setEditedItem(null);
            }
            setOpDataRealState(false);
            const updatedItems = cartItems.filter(ci => ci !== item);
            setCartItems(updatedItems);
            cartItemsRef.current = updatedItems;
            getPriceForCartItem();
        },
        addCurrentItem: () => {
            if (pendingUnityResults.current > 0) {
                // Friss Unity-eredményre várunk (pl. a gomb blur-je most frissített egy
                // paramétert) → a commit a friss cabinetResult megérkezésekor fut le
                // (lásd a halasztott-commit useEffect-et).
                pendingCommitRef.current = true;
                // FONTOS: itt NEM állítunk cartLoading-ot! A loadCartItems/updateCartFinalPrice
                // újrabelépés-védelme (`if (cartLoading) return`) miatt egy itt bekapcsolt flag
                // a halasztott commit merge-ágán elnyelné a loadCartItems() hívást → az opcut
                // nem frissülne → a REMAINDER sorok elavulnának. A loading-ot a tényleges
                // backend-hívás (getPriceForCartItem) maga kezeli.
                // Védőháló: ha a Unity-válasz elmaradna, a gomb ne "haljon meg" — a korábbi
                // (halasztás nélküli) viselkedésre esünk vissza az akkor aktuális eredménnyel.
                setTimeout(() => {
                    if (pendingCommitRef.current) {
                        console.warn("[CartHandler] Unity result timeout — committing with current cabinetResult.");
                        pendingCommitRef.current = false;
                        // Beragadt számláló feloldása: ha a Unity nem válaszolt (pl. no-op
                        // küldésre nem jön UCOrderReceived), ne blokkolja a KÖVETKEZŐ
                        // kattintásokat is — nulláról indulunk.
                        pendingUnityResults.current = 0;
                        commitRef.current();
                    }
                }, 4000);
                return;
            }
            commitCurrentConfiguration();
        },
        updateItemCount: (item: CartItem, newCount: number) => {
            if (!isCabinetItem(item)) return;
            setOpDataRealState(false);
            const updatedItem = { ...item, count: newCount };
            getPriceForCartItem(updatedItem);
        },
        clearCart: () => {
            setEditedItem(null);
            setCartItems([]);
            setOpData(false);
            setHwData(null);
            setOpDataRealState(false);
            cartItemsRef.current = [];
            saveCartToDatabase(false);
        },
        cartLoading,
        setCartLoading,
        generateShareLink,
        cartSaving,
        saveCart,
        isQuote,
        quote,
        quoteCart,
        createQuote,
        getSavedQuote,
        deleteQuote,
        orderQuote,
    }), [cartItems, editedItem, cartName, cabinetResult, dataContext, opData, hwData, opDataRealState, opDataRealFlag, cartLoading, cartSaving, currentConfigItem, currentConfigPrice, updateCurrentConfigItem, updateCurrentConfigPrice, isQuote, quote, quoteCart, designStyle, adminDesign]);

    async function loadQuoteCart(token: string): Promise<CartItem[]> {
        const url = PrestaGlobals.cartUrl("loadquotecart") + "&token=" + encodeURIComponent(token);
        setCartLoading(true);
        try {
            const response = await fetch(url);
            const data = await response.json();
            setCartLoading(false);
            if (data.error) {
                showQuoteError(data.error);
                return new Promise<CartItem[]>(() => {});
            }
            setIsQuote(true);
            setQuote(data.quote ?? null);
            setQuoteCart(data.quoteCart ?? null);
            setOpData(data.opcut);
            setHwData(data.hardware ?? null);
            setOpDataRealState(false);
            return data.cart ?? [];
        } catch {
            setCartLoading(false);
            setQuoteError(QUOTE_LOAD_FAILED);
            return new Promise<CartItem[]>(() => {});
        }
    }

    // Megrendelés: server copies the quote into the visitor's cart, then on to the PS cart
    async function orderQuote(): Promise<void> {
        const token = PrestaGlobals.quoteToken;
        const url = PrestaGlobals.cartUrl("orderquotecart") + "&token=" + encodeURIComponent(token);
        try {
            const data = await (await fetch(url)).json();
            if (data.error || !data.id_cart) {
                showQuoteError(data.error ?? QUOTE_ORDER_FAILED);
                return;
            }
            location.href = PrestaGlobals.cartURL;
        } catch {
            setQuoteError(QUOTE_ORDER_FAILED);
        }
    }

    // Quote: send the first cabinet's saved scripts to Unity (same path as editItem, read-only)
    function showQuoteCabinet(items: CartItem[]): void {
        const item = items.find(isCabinetItem);
        if (!item || !item.descriptor || !item.uconScript) return;
        const productId = Number(item.descriptor[0]?.productId);
        if (productId > 0) PrestaGlobals.productId = productId;
        PrestaGlobals.productName = item.Name;
        PrestaGlobals.savedTimestamp = item.id_cabinet;
        restoreDesignScripts(item);
        dataContext.updateScripts(item.descriptor, item.uconScript);
    }

    // A line saved from admin scripts reopens with its own StyleScript (UnityView sends designStyle)
    function restoreDesignScripts(item: CartItem): void {
        const style = item.styleScript ?? null;
        setDesignStyle(style);
        setAdminDesign(style != null);
    }

    // Admin script design: the cart is emptied, the current design alone becomes the quote
    async function quoteCurrentDesign(input: IQuoteInput): Promise<IQuoteResult> {
        if (pendingUnityResults.current > 0) {
            // Same as Kosárba: wait for the fresh result, unstick after 4 s
            await new Promise<void>((resolve) => {
                const timer = setTimeout(() => {
                    console.warn("[CartHandler] Unity result timeout — quoting with current cabinetResult.");
                    pendingUnityResults.current = 0;
                    unityWaiterRef.current = null;
                    resolve();
                }, 4000);
                unityWaiterRef.current = () => {
                    clearTimeout(timer);
                    unityWaiterRef.current = null;
                    resolve();
                };
            });
        }
        const result = cabinetResultRef.current;
        if (result == null) {
            return { url: '', error: 'A Unity még nem adott eredményt a tervre, várjon a 3D betöltésére.' };
        }
        const item = createCartData(getTimestamp(), "add", result, dataContext);
        if (item == null) {
            return { url: '', error: 'A jelenlegi tervből nem készíthető kosárelem.' };
        }
        item.count = 1;
        setEditedItem(null);
        setOpDataRealState(false);

        // Price it as Kosárba does: getPrice adds the board fields opcut needs (minWidth, Sheet*, ...)
        const url = PrestaGlobals.cartUrl("recalccartquickprice");
        let priced: any = null;
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: "payload=" + encodeURIComponent("[]") + "&item=" + encodeURIComponent(JSON.stringify(item)),
            });
            priced = response.ok ? await response.json().catch(() => null) : null;
        } catch {
            priced = null;
        }
        if (!priced || !Array.isArray(priced.cart) || priced.cart.length === 0) {
            return { url: '', error: 'A terv árazása sikertelen.' };
        }

        const items = priced.cart as CartItem[];
        cartItemsRef.current = items;
        setCartItems(items);
        setOpData(priced.opcut);
        setHwData(priced.hardware ?? null);
        return createQuote(input);
    }

    // Admin: snapshot the saved cart as a quote with a fixed gross total
    async function createQuote(input: IQuoteInput): Promise<IQuoteResult> {
        // Quote cart: saveCart is a no-op, server updates the quote from the stored JSON
        if (!(await saveCart())) {
            return { url: '', error: 'A kosár mentése / feldolgozása sikertelen, az árajánlat nem készült el.' };
        }
        const url = PrestaGlobals.cartUrl("createquote");
        try {
            // Multipart: carries the preview image
            const body = new FormData();
            body.append('quote_total', String(input.total));
            body.append('email', input.email.trim());
            body.append('valid_until', input.validUntil);
            body.append('link_expires_at', input.linkExpiresAt);
            body.append('guest_mode', String(input.guestMode));
            body.append('description', input.description);
            body.append('price_info', input.priceInfo);
            if (input.image) body.append('og_image', input.image);
            else if (input.removeImage) body.append('og_image_remove', '1');
            const response = await fetch(url, { method: "POST", body });
            const data = await response.json();
            if (data.error) return { url: '', error: data.error };
            return { url: data.quoteUrl ?? '', urlLong: data.quoteUrlLong ?? '', updated: !!data.updated };
        } catch {
            return { url: '', error: 'Hálózati hiba.' };
        }
    }

    async function getSavedQuote(): Promise<IQuoteSaved | null> {
        const url = PrestaGlobals.cartUrl("getquote");
        try {
            const response = await fetch(url);
            const data = await response.json();
            return data.quote ?? null;
        } catch {
            return null;
        }
    }

    // Admin: delete this cart's quote(s); the cart becomes a regular one
    async function deleteQuote(): Promise<{ deleted: number; error?: string }> {
        const url = PrestaGlobals.cartUrl("deletequote");
        try {
            const response = await fetch(url, { method: "POST" });
            const data = await response.json();
            if (data.error) return { deleted: 0, error: data.error };
            setIsQuote(false);
            setQuote(null);
            setQuoteCart(null);
            return { deleted: Number(data.deleted) || 0 };
        } catch {
            return { deleted: 0, error: 'Hálózati hiba.' };
        }
    }

    async function loadSharedCart(token: string): Promise<CartItem[]> {
        const url = PrestaGlobals.cartUrl("loadsharedcart") + "&token=" + encodeURIComponent(token);
        setCartLoading(true);
        try {
            const response = await fetch(url);
            const data = await response.json();
            setCartLoading(false);
            if (data.error) return [];
            setIsQuote(false);
            setOpData(data.opcut);
            setHwData(data.hardware ?? null);
            setOpDataRealState(false);
            return data.cart ?? [];
        } catch {
            setCartLoading(false);
            return [];
        }
    }

    async function loadUserCart(cartId: string, cabinetId?: string): Promise<CartItem[]> {
        let url = PrestaGlobals.cartUrl("loadusercart") + "&cartId=" + encodeURIComponent(cartId);
        if (cabinetId) url += "&cabinetId=" + encodeURIComponent(cabinetId);
        setCartLoading(true);
        try {
            const response = await fetch(url);
            const data = await response.json();
            setCartLoading(false);
            setIsQuote(!!data.isQuote);
            setOpData(data.opcut ?? null);
            setHwData(data.hardware ?? null);
            setOpDataRealState(false);
            return data.cart ?? [];
        } catch {
            setCartLoading(false);
            return [];
        }
    }

    async function generateShareLink(): Promise<string> {
        const url = PrestaGlobals.cartUrl("generatesharetoken");
        try {
            const response = await fetch(url);
            const data = await response.json();
            return data.shareUrl ?? '';
        } catch {
            return '';
        }
    }

    // Load cart items asynchronously from backend
    async function loadCartItems(): Promise<CartItem[]> {
        const url: string = PrestaGlobals.cartUrl("load");
        if (cartLoading) {
            // Prevent multiple simultaneous loads
            return Promise.resolve([]);
        }
        setCartLoading(true);
        try {
            const response = await fetch(url);
            const response_1 = await response.json();
            setCartLoading(false);
            if (response_1.error) {
                // Quote copy no longer accessible; server moved us to a fresh cart
                showQuoteError(response_1.error);
                return new Promise<CartItem[]>(() => {});
            }
            setIsQuote(!!response_1.isQuote);
            setQuote(response_1.quote ?? null);
            setQuoteCart(response_1.quoteCart ?? null);
            setOpData(response_1.opcut);
            setHwData(response_1.hardware ?? null);
            setOpDataRealState(false);
            return response_1.cart;
        } catch (error) {
            console.error("Error loading cart items:", error);
            setCartLoading(false);
            return [];
        }
    }

    function updateCartFinalPrice(): Promise<CartItem[]> {
        const url: string = PrestaGlobals.cartUrl("calcfinalprice");
        if (quoteReadOnly()) {
            return Promise.resolve(cartItemsRef.current.filter(isCabinetItem) as CartItem[]);
        }
        if (cartLoading) {
            // Prevent multiple simultaneous loads
            return Promise.resolve([]);
        }
        setCartLoading(true);
        if(!prestaGlobals.isLoggedIn) {
            location.href = PrestaGlobals.cartURL;
            // return Promise.resolve([]);
        }
        return fetch(url)
            .then((response: Response) => response.json())
            .then((response) => {
                setCartLoading(false);
                setOpData(response.opcut);
                setHwData(response.hardware ?? null);
                setOpDataRealState(true);
                setOpDataRealFlag(response.opcutReal === 1);
                return response.cart;
            })
            .catch((error: any) => {
                console.error("Error loading cart items:", error);
                setCartLoading(false);
                return [];
            });
    }

    // Update cart item map when cart items change, and save to session storage
    React.useEffect(() => {
        // Keep ref in sync with state
        cartItemsRef.current = cartItems;
    }, [cartItems]);

    // Auto-derive RemainderItems from OpData whenever opData changes
    React.useEffect(() => {
        const remainderItems = createRemainderItemsFromOpData(opData);
        setCartItems(prev => {
            const nonRemainders = prev.filter(ci => !(ci instanceof RemainderItem));
            return [...nonRemainders, ...remainderItems];
        });
    }, [opData]);

    // Auto-derive HardwareItems from the backend hardware plan. Order: cabinets -> hardware -> remainders
    React.useEffect(() => {
        const hardwareItems = createHardwareItemsFromPlan(hwData);
        setCartItems(prev => {
            const cabinets = prev.filter(isCabinetItem);
            const remainders = prev.filter(ci => ci instanceof RemainderItem);
            return [...cabinets, ...hardwareItems, ...remainders];
        });
    }, [hwData]);

    // Update cart name ref when cart name changes
    React.useEffect(() => {
        // Keep ref in sync with state
        cartNameRef.current = cartName;
        PrestaGlobals.cartReference = cartName; // Update global variable for use in other parts of the app
    }, [cartName]);
    
    React.useEffect(() => {
        // Initialize cart name from global variable
        // console.log("CartHandler init - cartReference:", PrestaGlobals.cartReference, "cartId:", PrestaGlobals.cartId);
        // setCartName(PrestaGlobals.cartReference !== "" ? PrestaGlobals.cartReference : ("#" + PrestaGlobals.cartId));
        // setCartName(PrestaGlobals.cartReference || ("#" + PrestaGlobals.cartId));

        // Load initial cart items — route to the appropriate loader based on URL params
        const urlParams = new URLSearchParams(window.location.search);
        const quoteToken = PrestaGlobals.quoteToken;
        const shareToken = urlParams.get('share');
        const cartId = urlParams.get('cart');
        const cabinetId = urlParams.get('cabinet');

        let loader: Promise<CartItem[]>;
        if (quoteToken) {
            loader = loadQuoteCart(quoteToken);
        } else if (shareToken) {
            loader = loadSharedCart(shareToken);
        } else if (cartId && cabinetId) {
            loader = loadCartItems().then((currentItems) =>
                loadUserCart(cartId, cabinetId).then((cabinetItems) => [
                    ...(currentItems ?? []),
                    ...(cabinetItems ?? []),
                ])
            );
        } else if (cartId) {
            loader = loadUserCart(cartId);
        } else {
            loader = loadCartItems();
        }

        loader.then((items: CartItem[]) => {
            const sorted = items.sort((a, b) => b.id_cabinet - a.id_cabinet);
            if (isQuoteRef.current && (quoteToken || !PrestaGlobals.isAdmin)) {
                showQuoteCabinet(sorted);
            }
            setCartItems(prev => [...sorted, ...prev.filter(ci => !isCabinetItem(ci))]);
            if (cartId && cabinetId) {
                // Two responses merged -> hardware/opcut cover only part of the cart; recalc once.
                cartItemsRef.current = sorted;
                getPriceForCartItem();
            }
            // ?quote stays: marks the quote view, F5 reloads the same copy
            if (shareToken || cartId) {
                window.history.replaceState({}, '', window.location.pathname);
            }
        });
    }, []);

    return (
        <CartContext.Provider value={cartContextValue}>
            {children}
            <QuoteErrorDialog message={quoteError} onClose={goHome} />
        </CartContext.Provider>
    );
};

export default CartHandler;
